import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { characters, getCharacter } from '../content/characters';
import { levels, isStageUnlocked, getLevel } from '../content/levels';
import { gameEvents, type CompletionState, type HudState } from '../game/events';
import type { GameRuntime } from '../game/createGame';
import { TouchControls } from '../components/TouchControls';
import { RoachPoster } from '../components/RoachPoster';
import { CharacterStandee } from '../components/CharacterStandee';
import { signInWithGoogle, signedInAlias, signOut } from '../services/authentication/auth';
import { isBackendConfigured } from '../services/backend/api';
import { syncGuestSave } from '../services/backend/cloudSave';
import { clearLeaderboardCache, loadLeaderboardPage, type LeaderboardEntry } from '../services/backend/leaderboard';
import { proceduralAudio } from '../services/audio/ProceduralAudio';
import { MISSION1_LEVEL_IDS, MISSION2_LEVEL_IDS } from '../content/missionObjectives';
import { guestSaveManager } from '../services/saves/GuestSaveManager';
import { bestStageForCharacter, missionScoreForCharacter } from '../services/saves/missionScore';
import { TabCoordinator } from '../services/saves/TabCoordinator';
import type { PlayerSettings } from '../services/saves/types';
import { shareResult } from '../services/sharing/shareResult';
import type { CharacterConfig } from '../types/content';

type Screen = 'menu' | 'select' | 'missions' | 'levels' | 'playing' | 'paused' | 'complete' | 'leaderboard';
type InitialRoute = { screen: Screen; missionId?: string; levelId?: string };

const emptyHud: HudState = {
  health: 3,
  wanted: 0,
  objective: 'Find the confiscation crate',
  missionPhase: 'find-mic',
  tokens: 0,
  abilityReady: true,
  abilityLabel: 'Ability',
  abilityHint: 'Press E near a marked opportunity',
  abilityStatus: 'READY',
  canCrouch: false,
  elapsedMs: 0,
};

function formatTime(milliseconds: number): string {
  const seconds = Math.max(0, Math.floor(milliseconds / 1000));
  return `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
}

function challengeFromUrl(): { characterId: string; score: number } | null {
  const raw = new URLSearchParams(window.location.search).get('challenge');
  if (!raw) return null;
  const [characterId, scoreText] = raw.split('.');
  const score = Number(scoreText);
  return characters.some((character) => character.id === characterId) && Number.isFinite(score) && score > 0
    ? { characterId, score }
    : null;
}

function missionIdForLevel(levelId: string): string {
  if ((MISSION2_LEVEL_IDS as readonly string[]).includes(levelId)) return 'seal-the-paper-trail';
  return 'recover-the-mic';
}

function pathForScreen(screen: Screen, missionId: string, levelId: string): string {
  switch (screen) {
    case 'select':
      return '/roaches';
    case 'missions':
      return '/missions';
    case 'levels':
      return `/missions/${missionId}`;
    case 'playing':
      return `/play/${levelId}`;
    case 'paused':
      return `/play/${levelId}/paused`;
    case 'complete':
      return `/result/${levelId}`;
    case 'leaderboard':
      return '/leaderboard';
    default:
      return '/';
  }
}

function routeFromPath(): InitialRoute {
  const path = window.location.pathname.replace(/\/+$/, '') || '/';
  const parts = path.split('/').filter(Boolean);
  if (path === '/roaches') return { screen: 'select' };
  if (path === '/missions') return { screen: 'missions' };
  if (path === '/leaderboard') return { screen: 'leaderboard' };
  if (parts[0] === 'missions' && parts[1]) {
    const missionId = parts[1] === 'seal-the-paper-trail' ? 'seal-the-paper-trail' : 'recover-the-mic';
    return { screen: 'levels', missionId };
  }
  if ((parts[0] === 'play' || parts[0] === 'result') && parts[1] && levels.some((level) => level.id === parts[1])) {
    return { screen: 'levels', missionId: missionIdForLevel(parts[1]), levelId: parts[1] };
  }
  return { screen: 'menu' };
}

function emitAnalyticsPageView(path: string, screen: Screen): void {
  const dataWindow = window as Window & { dataLayer?: Array<Record<string, unknown>> };
  dataWindow.dataLayer?.push({
    event: 'page_view',
    page_path: path,
    page_title: `Project Roach - ${screen}`,
  });
}

function consumeAuthStatusFromUrl(): string {
  const url = new URL(window.location.href);
  const status = url.searchParams.get('auth');
  if (!status) return '';
  url.searchParams.delete('auth');
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
  if (status === 'cancelled') return 'Google sign-in cancelled. You can keep playing as guest.';
  return 'Google sign-in could not finish. Please try again.';
}

function verificationCopy(status: CompletionState['verificationStatus'], signedIn: boolean, backendConfigured: boolean): string {
  switch (status) {
    case 'submitting':
      return 'Sending verified run to the weekly register…';
    case 'submitted':
      return 'Submitted to this week\'s board.';
    case 'review':
      return 'Held for review — local score still counts.';
    case 'failed':
      return 'Online verify failed. Local score is safe.';
    default:
      if (!backendConfigured) return 'Local only - weekly board is not configured in this build.';
      if (signedIn) return 'Local only - online verification was not available when this run started.';
      return 'Local only — sign in before the next run for the weekly board.';
  }
}

function clampButtonScale(value: number): number {
  return Math.min(1.35, Math.max(0.75, Number.isFinite(value) ? value : 1));
}

export function GameApp() {
  const canvasHost = useRef<HTMLDivElement>(null);
  const characterList = useRef<HTMLDivElement>(null);
  const characterCards = useRef<Partial<Record<CharacterConfig['id'], HTMLButtonElement | null>>>({});
  const characterScrollTimer = useRef<number | null>(null);
  const runtime = useRef<GameRuntime | null>(null);
  const gameReady = useRef(false);
  const gameReadyWaiters = useRef<Array<() => void>>([]);
  const saveAtStart = useMemo(() => guestSaveManager.load(), []);
  const initialRoute = useMemo(routeFromPath, []);
  const initialLevelId = initialRoute.levelId ?? saveAtStart.payload.checkpoint?.levelId ?? 'mic-01-chai-drill';
  const initialMissionId = initialRoute.missionId ?? missionIdForLevel(initialLevelId);
  const screenRef = useRef<Screen>(initialRoute.screen);
  const screenHistory = useRef<Screen[]>([]);
  const activeLevelId = useRef(initialLevelId);
  const [gameStarting, setGameStarting] = useState(false);
  const [screen, setScreenState] = useState<Screen>(initialRoute.screen);
  const [selectedCharacter, setSelectedCharacter] = useState<CharacterConfig['id']>(saveAtStart.payload.selectedCharacter);
  const [selectedMissionId, setSelectedMissionId] = useState(initialMissionId);
  const [selectedLevelId, setSelectedLevelId] = useState(initialLevelId);
  const [hud, setHud] = useState<HudState>(emptyHud);
  const [completion, setCompletion] = useState<CompletionState | null>(null);
  const [settings, setSettings] = useState<PlayerSettings>(saveAtStart.payload.settings);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [disclaimerOpen, setDisclaimerOpen] = useState(!saveAtStart.payload.disclaimerAccepted);
  const [tabOwner, setTabOwner] = useState(true);
  const [toast, setToast] = useState('');
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);
  const [leaderboardError, setLeaderboardError] = useState(false);
  const [leaderboardPage, setLeaderboardPage] = useState(1);
  const [leaderboardTotalPages, setLeaderboardTotalPages] = useState(1);
  const [leaderboardTotal, setLeaderboardTotal] = useState(0);
  const [leaderboardPageSize, setLeaderboardPageSize] = useState(50);
  const [leaderboardMaxEntries, setLeaderboardMaxEntries] = useState(1000);
  const [highScores, setHighScores] = useState(saveAtStart.payload.highScores);
  const [alias, setAlias] = useState<string | null>(null);
  const tabCoordinator = useRef<TabCoordinator | null>(null);
  const challenge = useMemo(challengeFromUrl, []);

  const refreshHighScores = () => setHighScores({ ...guestSaveManager.load().payload.highScores });
  const browserPathFor = (next: Screen) => pathForScreen(next, selectedMissionId, activeLevelId.current || selectedLevelId);
  const pushBrowserState = (next: Screen, replace = false, path = browserPathFor(next)) => {
    const state = { projectRoachScreen: next };
    try {
      if (replace) window.history.replaceState(state, '', path);
      else window.history.pushState(state, '', path);
      emitAnalyticsPageView(path, next);
    } catch {
      // Some embedded browsers restrict history mutations; in-app navigation still works.
    }
  };
  const applyScreen = (next: Screen) => {
    screenRef.current = next;
    setScreenState(next);
  };
  const navigateToScreen = (next: Screen, options: { replace?: boolean; browser?: boolean; path?: string } = {}) => {
    const current = screenRef.current;
    if (current === next) return;
    if (!options.replace) screenHistory.current.push(current);
    applyScreen(next);
    if (!options.browser) pushBrowserState(next, Boolean(options.replace), options.path);
  };
  const stopActiveRun = () => {
    gameEvents.emit('ui:menu', undefined);
    proceduralAudio.stopMusic();
    exitLevelFullscreen();
    refreshHighScores();
  };
  const navigateBack = () => {
    const current = screenRef.current;
    const previous = screenHistory.current.pop() ?? 'menu';
    if (current === 'playing' || current === 'paused') stopActiveRun();
    const next = previous === 'playing' || previous === 'paused' ? 'levels' : previous;
    applyScreen(next);
    emitAnalyticsPageView(window.location.pathname, next);
  };
  const requestBackNavigation = () => {
    if (!screenHistory.current.length) {
      navigateBack();
      return;
    }
    try {
      window.history.back();
    } catch {
      navigateBack();
    }
  };
  const enterLevelFullscreen = () => {
    const root = document.documentElement as HTMLElement & { webkitRequestFullscreen?: () => Promise<void> | void };
    if (document.fullscreenElement) return;
    const requestFullscreen = root.requestFullscreen ?? root.webkitRequestFullscreen;
    if (!requestFullscreen) return;
    try {
      const request = requestFullscreen.call(root);
      if (request && typeof (request as Promise<void>).catch === 'function') {
        void (request as Promise<void>).catch(() => undefined);
      }
    } catch {
      // Fullscreen is a progressive mobile enhancement; blocked requests should not block play.
    }
  };
  const exitLevelFullscreen = () => {
    if (!document.fullscreenElement || !document.exitFullscreen) return;
    void document.exitFullscreen().catch(() => undefined);
  };

  useEffect(() => {
    const handleBrowserBack = () => navigateBack();
    window.addEventListener('popstate', handleBrowserBack);
    const offReady = gameEvents.on('game:ready', () => {
      gameReady.current = true;
      for (const resolve of gameReadyWaiters.current.splice(0)) resolve();
    });
    const offStarted = gameEvents.on('game:started', () => navigateToScreen('playing', { replace: screenRef.current === 'complete', path: pathForScreen('playing', missionIdForLevel(activeLevelId.current), activeLevelId.current) }));
    const offHud = gameEvents.on('game:hud', setHud);
    const offPaused = gameEvents.on('game:paused', ({ paused }) => navigateToScreen(paused ? 'paused' : 'playing', { replace: true }));
    const offComplete = gameEvents.on('game:complete', (result) => {
      proceduralAudio.stopMusic();
      exitLevelFullscreen();
      setCompletion(result);
      activeLevelId.current = result.levelId;
      refreshHighScores();
      navigateToScreen('complete', { replace: true, path: pathForScreen('complete', selectedMissionId, result.levelId) });
    });
    const offVerification = gameEvents.on('game:verification', ({ status }) => {
      if (status === 'submitted') clearLeaderboardCache();
      setCompletion((current) => (current ? { ...current, verificationStatus: status } : current));
    });
    const offToast = gameEvents.on('game:toast', ({ message }) => setToast(message));
    const offAudio = gameEvents.on('game:audio', ({ kind }) => proceduralAudio.play(kind));
    const authMessage = consumeAuthStatusFromUrl();
    if (authMessage) setToast(authMessage);
    pushBrowserState(screenRef.current, true, pathForScreen(screenRef.current, initialMissionId, initialLevelId));
    if (screenRef.current === 'leaderboard') void loadLeaderboard(1);
    signedInAlias().then((name) => {
      setAlias(name);
      if (name) {
        void syncGuestSave(guestSaveManager.load()).then(() => refreshHighScores()).catch(() => undefined);
      }
    }).catch(() => setAlias(null));

    const coordinator = new TabCoordinator();
    tabCoordinator.current = coordinator;
    const stopCoordinator = coordinator.start(setTabOwner);
    return () => {
      offReady(); offStarted(); offHud(); offPaused(); offComplete(); offVerification(); offToast(); offAudio();
      if (characterScrollTimer.current) window.clearTimeout(characterScrollTimer.current);
      window.removeEventListener('popstate', handleBrowserBack);
      proceduralAudio.stopMusic();
      stopCoordinator();
      runtime.current?.destroy();
    };
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle('reduce-motion', settings.reducedMotion);
    proceduralAudio.configure(settings);
  }, [settings.reducedMotion]);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(''), 2800);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const persistSettings = (next: PlayerSettings) => {
    setSettings(next);
    guestSaveManager.save((payload) => ({ ...payload, settings: next }));
  };
  const updateSetting = <Key extends keyof PlayerSettings>(key: Key, value: PlayerSettings[Key]) => {
    persistSettings({ ...settings, [key]: value });
  };

  const chooseCharacter = (id: CharacterConfig['id'], scrollToCard = false) => {
    setSelectedCharacter(id);
    guestSaveManager.save((payload) => ({ ...payload, selectedCharacter: id }));
    if (scrollToCard) {
      window.setTimeout(() => {
        characterCards.current[id]?.scrollIntoView({
          behavior: settings.reducedMotion ? 'auto' : 'smooth',
          block: 'nearest',
          inline: 'center',
        });
      }, 0);
    }
  };

  const syncCharacterFromScroll = () => {
    if (!characterList.current) return;
    if (characterScrollTimer.current) window.clearTimeout(characterScrollTimer.current);
    characterScrollTimer.current = window.setTimeout(() => {
      const listBounds = characterList.current?.getBoundingClientRect();
      if (!listBounds) return;
      const listCenter = listBounds.left + listBounds.width / 2;
      const nearest = characters.reduce<{ id: CharacterConfig['id']; distance: number } | null>((current, character) => {
        const card = characterCards.current[character.id];
        if (!card) return current;
        const bounds = card.getBoundingClientRect();
        const distance = Math.abs(bounds.left + bounds.width / 2 - listCenter);
        return !current || distance < current.distance ? { id: character.id, distance } : current;
      }, null);
      if (nearest && nearest.id !== selectedCharacter) chooseCharacter(nearest.id);
    }, 90);
  };

  const ensureGameRuntimeReady = async () => {
    if (gameReady.current) return;
    if (!runtime.current) {
      if (!canvasHost.current) throw new Error('Game canvas is not ready.');
      const { createGame } = await import('../game/createGame');
      runtime.current = createGame(canvasHost.current);
    }
    if (gameReady.current) return;
    await new Promise<void>((resolve) => gameReadyWaiters.current.push(resolve));
  };

  const startGame = async (resume = false, levelId = selectedLevelId) => {
    if (gameStarting) return;
    enterLevelFullscreen();
    setGameStarting(true);
    setToast('Loading the gali...');
    if (!tabOwner) {
      tabCoordinator.current?.takeOwnership();
      setTabOwner(true);
    }
    setCompletion(null);
    setHud(emptyHud);
    try {
      await ensureGameRuntimeReady();
      void proceduralAudio.startMusic(settings);
      activeLevelId.current = levelId;
      setSelectedLevelId(levelId);
      gameEvents.emit('ui:start-game', { characterId: selectedCharacter, levelId, resume });
    } catch {
      setToast('Could not load the gali. Refresh and try again.');
    } finally {
      setGameStarting(false);
    }
  };

  const returnToMenu = () => {
    gameEvents.emit('ui:menu', undefined);
    proceduralAudio.stopMusic();
    exitLevelFullscreen();
    screenHistory.current = [];
    navigateToScreen('menu', { replace: true });
    setCompletion(null);
    refreshHighScores();
  };

  const acceptDisclaimer = () => {
    guestSaveManager.save((payload) => ({ ...payload, disclaimerAccepted: true }));
    setDisclaimerOpen(false);
  };

  const loadLeaderboard = async (page = leaderboardPage) => {
    if (!isBackendConfigured()) {
      setLeaderboard([]);
      setLeaderboardPage(1);
      setLeaderboardTotalPages(1);
      setLeaderboardTotal(0);
      return;
    }
    setLeaderboardLoading(true);
    setLeaderboardError(false);
    try {
      const data = await loadLeaderboardPage(page);
      setLeaderboard(data.entries);
      setLeaderboardPage(data.page);
      setLeaderboardTotalPages(data.totalPages);
      setLeaderboardTotal(data.total);
      setLeaderboardPageSize(data.pageSize);
      setLeaderboardMaxEntries(data.maxEntries);
    } catch {
      setLeaderboardError(true);
      setToast('Leaderboard abhi chai break par hai.');
      setLeaderboard([]);
    } finally {
      setLeaderboardLoading(false);
    }
  };

  const openLeaderboard = () => {
    navigateToScreen('leaderboard');
    void loadLeaderboard(leaderboardPage);
  };

  const changeLeaderboardPage = (page: number) => {
    void loadLeaderboard(Math.max(1, Math.min(leaderboardTotalPages, page)));
  };

  const handleSignIn = async () => {
    const result = await signInWithGoogle();
    setToast(result.message);
  };

  const handleSignOut = async () => {
    try {
      const result = await signOut();
      setAlias(null);
      clearLeaderboardCache();
      setLeaderboard([]);
      setLeaderboardPage(1);
      setLeaderboardTotalPages(1);
      setLeaderboardTotal(0);
      setToast(result.message);
    } catch {
      setToast('Could not log out. Try again in a moment.');
    }
  };

  const handleShare = async () => {
    if (!completion) return;
    try {
      setToast(await shareResult(completion));
    } catch (error) {
      setToast(error instanceof Error && error.name === 'AbortError' ? 'Share cancelled.' : 'Could not create the poster.');
    }
  };

  const selected = getCharacter(selectedCharacter);
  const canResume = Boolean(guestSaveManager.load().payload.checkpoint);
  const completedLevels = guestSaveManager.load().payload.completedLevels;
  const missionLevels = levels.filter((level) => level.missionId === selectedMissionId);
  const missionTitle = selectedMissionId === 'seal-the-paper-trail' ? 'Seal the Paper Trail' : 'Recover the Mic';
  const nextAfterComplete = completion
    ? levels.find((level) => level.missionId === getLevel(completion.levelId).missionId && level.stageOrder === (getLevel(completion.levelId).stageOrder + 1))
    : undefined;
  const bestForCharacter = (characterId: CharacterConfig['id']) => bestStageForCharacter(highScores, characterId);
  const bestForLevel = (levelId: string) => {
    const scores = characters.map((character) => highScores[`${levelId}:${character.id}`] ?? 0).filter((score) => score > 0);
    return scores.length ? Math.max(...scores) : 0;
  };
  const missionForSelected = missionScoreForCharacter(highScores, selectedCharacter, MISSION1_LEVEL_IDS);
  const mission2ForSelected = missionScoreForCharacter(highScores, selectedCharacter, MISSION2_LEVEL_IDS);

  return (
    <main class={`game-app game-app--${screen}`}>
      <div ref={canvasHost} class="game-canvas" aria-hidden={screen !== 'playing' && screen !== 'paused'} />
      <div class="paper-noise" />

      {(screen === 'menu' || screen === 'select' || screen === 'missions' || screen === 'levels' || screen === 'leaderboard') && (
        <header class="brand-strip">
          <button class="brand-mark" type="button" onClick={returnToMenu}>PROJECT ROACH</button>
          <div class="brand-strip__actions">
            {alias && <span class="account-alias">{alias}</span>}
            {alias && <button class="text-button" type="button" onClick={handleSignOut}>Log out</button>}
            <button class="text-button" type="button" onClick={() => setSettingsOpen(true)}>Settings</button>
          </div>
        </header>
      )}

      {screen === 'menu' && (
        <section class="menu-screen screen-enter">
          <div class="menu-copy">
            <p class="eyebrow">A FICTIONAL CIVIC-SATIRE PLATFORMER</p>
            <h1>MIC SAFE.<br /><em>ROACH OUT.</em></h1>
            <p class="menu-lede">Recover the People's Mic. Outsmart an absurd system. Escape Dilli before the van catches up.</p>
            <div class="mission-route" aria-label="Mission route">
              <span>CHAI</span><i /> <span>JANTAR MANTAR</span><i /> <span>MIC</span><i /> <span>STAGE</span><i /> <span>METRO</span>
            </div>
            <div class="delhi-reference-line"><b>28.6304° N · CENTRAL DELHI</b><span>JANTAR MANTAR</span><span>CONNAUGHT PLACE COLONNADES</span><span>BARAKHAMBA ROAD</span></div>
            {challenge && (
              <p class="challenge-line">Challenge received: beat <strong>{challenge.score.toLocaleString()}</strong> points.</p>
            )}
            <div class="menu-actions">
              <button class="primary-button" type="button" onClick={() => navigateToScreen('select')}>
                Choose your roach
              </button>
              {canResume && <button class="secondary-button" type="button" disabled={gameStarting} onClick={() => void startGame(true)}>{gameStarting ? 'Loading...' : 'Resume checkpoint'}</button>}
              <button class="text-button text-button--large" type="button" onClick={openLeaderboard}>Weekly leaderboard →</button>
            </div>
            <p class="instant-note">No login. No download. Bas bhaago.</p>
          </div>
          <div class="menu-art"><CharacterStandee characterId={selectedCharacter} accent={`#${selected.accent.toString(16).padStart(6, '0')}`} /></div>
          <footer class="legal-line">
            Fictional parody—not factual reporting. <a href="/legal/privacy.html" target="_blank">Privacy</a> · <a href="/legal/terms.html" target="_blank">Terms</a> · <a href="mailto:report@projectroach.invalid">Report content</a>
          </footer>
        </section>
      )}

      {screen === 'select' && (
        <section class="character-screen screen-enter">
          <div class="character-heading">
            <button class="back-button" type="button" onClick={requestBackNavigation}>← Back</button>
            <p class="eyebrow">THREE METHODS. ONE MIC.</p>
            <h2>Choose your<br />survival strategy.</h2>
          </div>
          <div class="character-list" ref={characterList} onScroll={syncCharacterFromScroll}>
            {characters.map((character, index) => {
              const best = bestForCharacter(character.id);
              const mission = missionScoreForCharacter(highScores, character.id);
              return (
                <button
                  type="button"
                  ref={(element) => {
                    characterCards.current[character.id] = element;
                  }}
                  class={`character-row${selectedCharacter === character.id ? ' character-row--selected' : ''}`}
                  onClick={() => chooseCharacter(character.id, true)}
                  style={{ '--character-accent': `#${character.accent.toString(16).padStart(6, '0')}` }}
                  aria-pressed={selectedCharacter === character.id}
                >
                  <span class="character-index">0{index + 1}</span>
                  <span class="dossier-stamp">FIELD DOSSIER</span>
                  <span class="character-model" aria-hidden="true">
                    <CharacterStandee characterId={character.id} accent={`#${character.accent.toString(16).padStart(6, '0')}`} compact />
                  </span>
                  <span class="character-title">
                    <strong>{character.name}</strong>
                    <small>{character.description}</small>
                    <em class="character-best">{mission.missionScore > 0
                      ? `Mission ${mission.missionScore.toLocaleString()} · ${mission.stagesCleared}/10${best > 0 ? ` · stage best ${best.toLocaleString()}` : ''}`
                      : 'No mission score yet'}</em>
                  </span>
                  <span class="character-kit"><b>{character.abilityLabel}</b><small>{character.passiveLabel}</small></span>
                  <span class="character-select-mark">{selectedCharacter === character.id ? 'SELECTED' : 'SELECT'}</span>
                </button>
              );
            })}
          </div>
          <div class="character-dots" aria-label="Roach options">
            {characters.map((character) => (
              <button
                type="button"
                class={selectedCharacter === character.id ? 'character-dot character-dot--selected' : 'character-dot'}
                onClick={() => chooseCharacter(character.id, true)}
                aria-label={`Select ${character.name}`}
              />
            ))}
          </div>
          <div class="character-confirm">
            <div>
              <span class="character-confirm__kicker">Selected roach</span>
              <strong>{selected.abilityLabel}</strong>
              <span>{selected.tradeOff}</span>
            </div>
            <button class="primary-button" type="button" onClick={() => navigateToScreen('missions')}>Choose mission</button>
          </div>
        </section>
      )}
      {screen === 'select' && (
        <div class="character-confirm character-confirm--floating" aria-label="Selected roach action">
          <div>
            <span class="character-confirm__kicker">Selected roach</span>
            <strong>{selected.abilityLabel}</strong>
            <span>{selected.tradeOff}</span>
          </div>
          <button class="primary-button" type="button" onClick={() => navigateToScreen('missions')}>Choose mission</button>
        </div>
      )}

      {screen === 'missions' && (
        <section class="level-screen screen-enter">
          <div class="level-heading">
            <button class="back-button" type="button" onClick={requestBackNavigation}>← Roach dossiers</button>
            <p class="eyebrow">DELHI CHAPTER / MISSION SELECT</p>
            <h2>Pick the<br />public nuisance.</h2>
          </div>
          <div class="level-posters">
            <button type="button" class="level-poster level-poster--delhi-mic-run" onClick={() => { setSelectedMissionId('recover-the-mic'); navigateToScreen('levels', { path: pathForScreen('levels', 'recover-the-mic', selectedLevelId) }); }}>
              <span class="level-poster__number">01</span>
              <small>MISSION AVAILABLE</small>
              <strong>Recover the Mic</strong>
              <p>Ten stages. Soft bonus routes. Bosses at 5 and 10.</p>
              {missionForSelected.missionScore > 0 && (
                <em class="level-poster__best">
                  {missionForSelected.fullClear ? 'FULL CLEAR · ' : ''}
                  Mission {missionForSelected.missionScore.toLocaleString()} · {missionForSelected.stagesCleared}/10
                </em>
              )}
              <b>OPEN STAGES →</b>
            </button>
            <button type="button" class="level-poster level-poster--paper-trail-run" onClick={() => { setSelectedMissionId('seal-the-paper-trail'); navigateToScreen('levels', { path: pathForScreen('levels', 'seal-the-paper-trail', selectedLevelId) }); }}>
              <span class="level-poster__number">02</span>
              <small>MISSION AVAILABLE</small>
              <strong>Seal the Paper Trail</strong>
              <p>Ten fictional New Delhi verification stages across Jantar Mantar and Connaught Place.</p>
              {mission2ForSelected.missionScore > 0 && (
                <em class="level-poster__best">
                  {mission2ForSelected.fullClear ? 'FULL CLEAR · ' : ''}
                  Mission {mission2ForSelected.missionScore.toLocaleString()} · {mission2ForSelected.stagesCleared}/{mission2ForSelected.stageCount}
                </em>
              )}
              <b>OPEN STAGES →</b>
            </button>
            <article class="level-poster level-poster--pending">
              <span class="level-poster__number">03</span>
              <small>PROGRESSION TEASER</small>
              <strong>Permission Pending</strong>
              <p>The correct form exists. Nobody has seen it.</p>
              <b>COMING LATER</b>
            </article>
          </div>
        </section>
      )}

      {screen === 'levels' && (
        <section class="level-screen screen-enter">
          <div class="level-heading"><button class="back-button" type="button" onClick={requestBackNavigation}>← Missions</button><p class="eyebrow">{selectedMissionId === 'seal-the-paper-trail' ? 'MISSION 2 / SEAL THE PAPER TRAIL' : 'MISSION 1 / RECOVER THE MIC'}</p><h2>Ten stages.<br />{missionTitle}.</h2></div>
          <div class="level-posters level-posters--mission">
            {missionLevels.map((level) => {
              const unlocked = isStageUnlocked(level.id, completedLevels);
              const best = bestForLevel(level.id);
              const done = completedLevels.includes(level.id);
              return <button type="button" class={`level-poster level-poster--${level.bossStage ? 'boss' : 'stage'}${unlocked ? '' : ' level-poster--locked'}`} disabled={!unlocked || gameStarting} onClick={() => void startGame(false, level.id)}>
                <span class="level-poster__number">{String(level.stageOrder).padStart(2, '0')}</span>
                <small>{done ? 'CLEARED' : unlocked ? (level.bossStage ? 'BOSS STAGE' : 'STAGE AVAILABLE') : 'CLEAR PREVIOUS'}</small>
                <strong>{level.name}</strong>
                <p>{level.shortDescription}</p>
                {unlocked && best > 0 && <em class="level-poster__best">BEST {best.toLocaleString()}</em>}
                <b>{unlocked ? (done ? 'REPLAY →' : 'PLAY STAGE →') : 'LOCKED'}</b>
              </button>;
            })}
          </div>
        </section>
      )}

      {(screen === 'playing' || screen === 'paused') && (
        <section class="game-ui" aria-label="Game status">
          <div class="hud-top">
            <div class="hud-objective"><span>MISSION</span><strong>{hud.objective}</strong></div>
            <div class="hud-stats">
              <div class="health-pips" aria-label={`${hud.health} health remaining`}>
                {[0, 1, 2].map((pip) => <i class={pip < hud.health ? 'health-pip health-pip--full' : 'health-pip'} />)}
              </div>
              <span class="hud-token">✣ {hud.tokens}</span>
              <span class="hud-time">{formatTime(hud.elapsedMs)}</span>
              <button class="pause-button" type="button" onClick={() => gameEvents.emit('ui:pause', undefined)} aria-label="Pause">Ⅱ</button>
            </div>
          </div>
          <div class="wanted-wrap"><span>WANTED</span><div class="wanted-track"><i style={{ width: `${hud.wanted}%` }} /></div><b>{hud.wanted}</b></div>
          {hud.boss?.active && (
            <div class={`boss-wrap boss-wrap--${hud.boss.phase}`}>
              <span>BOSS</span>
              <strong>{hud.boss.name}</strong>
              <div class="boss-hp" aria-label={`${hud.boss.hp} of ${hud.boss.maxHp} hits left`}>
                {Array.from({ length: hud.boss.maxHp }, (_, index) => (
                  <i class={index < hud.boss!.hp ? 'boss-pip boss-pip--full' : 'boss-pip'} />
                ))}
              </div>
              <b>{hud.boss.phase === 'vulnerable' ? 'TIRED' : hud.boss.phase === 'down' ? 'STUNNED' : hud.boss.phase.toUpperCase()}</b>
            </div>
          )}
          {hud.chase?.active && (
            <div class={`chase-warn${hud.chase.released ? ' chase-warn--hot' : ''}`} role="status">
              <span>VAN INCOMING</span>
              <strong>{hud.chase.released ? 'ON YOUR TAIL' : 'ARMED — MOVE RIGHT'}</strong>
              <div class="chase-distance"><i style={{ width: `${Math.min(100, Math.max(8, 100 - hud.chase.distancePx / 8))}%` }} /></div>
              {hud.chase.graceMs > 0 && <small>Grace {Math.ceil(hud.chase.graceMs / 100)}0ms</small>}
            </div>
          )}
          <div class={`ability-chip${hud.abilityReady ? ' ability-chip--ready' : ''}`}><span>{hud.abilityLabel}</span><small>{hud.abilityHint}</small><b>{hud.abilityStatus}</b></div>
          {hud.message && <div class="game-message">{hud.message}</div>}
          <TouchControls
            leftHanded={settings.leftHanded}
            canCrouch={hud.canCrouch}
            jumpButtonScale={clampButtonScale(settings.jumpButtonScale)}
            actionButtonScale={clampButtonScale(settings.actionButtonScale)}
          />
        </section>
      )}

      {screen === 'paused' && (
        <section class="modal-layer pause-layer screen-enter" role="dialog" aria-modal="true" aria-label="Game paused">
          <div class="pause-copy"><p class="eyebrow">TIMEOUT, BOSS</p><h2>Gali paused.</h2><p>The wanted meter is also pretending to work.</p></div>
          <div class="pause-actions">
            <button class="primary-button" type="button" onClick={() => gameEvents.emit('ui:resume', undefined)}>Resume</button>
            <button class="secondary-button" type="button" onClick={() => setSettingsOpen(true)}>Settings</button>
            <button class="text-button text-button--large" type="button" onClick={returnToMenu}>Quit to menu</button>
          </div>
        </section>
      )}

      {screen === 'complete' && completion && (
        <section class="complete-screen screen-enter">
          <div class="complete-stamp">MISSION / COMPLETE</div>
          <div class="complete-copy">
            <p class="eyebrow">{completion.levelName.toUpperCase()} / ROACH UNAVAILABLE FOR COMMENT.</p>
            <h2>{completion.score.total.toLocaleString()}</h2>
            <p class="result-label">POINTS</p>
            <p class={`personal-best-line${completion.isNewBest ? ' personal-best-line--new' : ''}`}>
              {completion.isNewBest ? 'New stage best' : 'Stage best'} · {completion.personalBest.toLocaleString()}
            </p>
            <p class="personal-best-line">
              Mission total · {completion.missionScore.toLocaleString()} · {completion.stagesCleared}/10 stages
            </p>
            <div class="result-line"><span>{completion.characterName}</span><span>{formatTime(completion.elapsedMs)}</span><span>{completion.tokens} tokens</span><span>{completion.respawns} retries</span></div>
            <div class="score-breakdown" aria-label="Score breakdown">
              <span>Completion {completion.score.completion.toLocaleString()}</span>
              <span>Mission {completion.score.mission.toLocaleString()}</span>
              <span>Tokens {completion.score.collectibles.toLocaleString()}</span>
              <span>Health {completion.score.health.toLocaleString()}</span>
              <span>Stealth {completion.score.stealth.toLocaleString()}</span>
              <span>Time {completion.score.time.toLocaleString()}</span>
              {completion.score.streak > 0 && <span>Streak +{completion.score.streak.toLocaleString()}</span>}
              {completion.score.routeBonus > 0 && <span>Route +{completion.score.routeBonus.toLocaleString()}</span>}
              {completion.score.enemyBonus > 0 && <span>Enemy +{completion.score.enemyBonus.toLocaleString()}</span>}
              <span>Penalties −{completion.score.penalties.toLocaleString()}</span>
            </div>
            <p class="verification-line">{verificationCopy(completion.verificationStatus, Boolean(alias), isBackendConfigured())}</p>
          </div>
          <div class="complete-art"><CharacterStandee characterId={completion.characterId} accent={`#${getCharacter(completion.characterId).accent.toString(16).padStart(6, '0')}`} compact /></div>
          <div class="complete-actions">
            <div class="complete-actions__primary">
              {nextAfterComplete && <button class="primary-button" type="button" disabled={gameStarting} onClick={() => void startGame(false, nextAfterComplete.id)}>Next stage: {nextAfterComplete.name}</button>}
              <button class="primary-button" type="button" onClick={handleShare}>Share result poster</button>
              <button class="secondary-button" type="button" disabled={gameStarting} onClick={() => void startGame(false, completion.levelId)}>Run again</button>
            </div>
            <div class="complete-actions__secondary">
              {!nextAfterComplete && completion && getLevel(completion.levelId).stageOrder >= 10 && (
                <p class="verification-line">{getLevel(completion.levelId).missionId === 'seal-the-paper-trail' ? 'Mission 2 complete. Further chapters stay permission-pending.' : 'Mission 1 complete. Paper Trail is now open from the mission map.'}</p>
              )}
              <button class="text-button text-button--large" type="button" onClick={() => navigateToScreen('missions')}>Mission map</button>
              {isBackendConfigured() && !alias && <button class="text-button text-button--large" type="button" onClick={handleSignIn}>Sign in for verified runs</button>}
              <button class="text-button text-button--large" type="button" onClick={returnToMenu}>Back to menu</button>
            </div>
          </div>
        </section>
      )}

      {screen === 'leaderboard' && (
        <section class="leaderboard-screen screen-enter">
          <div class="leaderboard-heading"><button class="back-button" type="button" onClick={requestBackNavigation}>← Back</button><p class="eyebrow">VERIFIED MISSION TOTALS</p><h2>This week's<br />pest ranking.</h2><p class="leaderboard-summary">Top {leaderboardMaxEntries.toLocaleString()} · {leaderboardPageSize} per page · {leaderboardTotal.toLocaleString()} ranked</p></div>
          <ol class="leaderboard-list leaderboard-list--mission">
            {leaderboardLoading && <li class="leaderboard-empty">Checking the official unofficial register…</li>}
            {!leaderboardLoading && leaderboardError && <li class="leaderboard-empty">Register is on chai break. Local scores are still safe.</li>}
            {!leaderboardLoading && !leaderboardError && !isBackendConfigured() && (
              <li class="leaderboard-empty">Cloud rankings are not configured yet. Local scores are still safe.</li>
            )}
            {!leaderboardLoading && !leaderboardError && isBackendConfigured() && leaderboard.length === 0 && (
              <li class="leaderboard-empty">
                {alias
                  ? 'No verified pests this week. Finish signed-in stages to stamp the mission board.'
                  : 'No verified pests this week. Sign in before a run to join the board.'}
              </li>
            )}
            {leaderboard.map((entry) => (
              <li class={entry.rank === 1 ? 'leaderboard-row--top' : ''}>
                <span>{String(entry.rank).padStart(2, '0')}</span>
                <strong>{entry.alias}</strong>
                <small>{entry.characterId}</small>
                <small class="leaderboard-level">{entry.fullClear ? 'FULL CLEAR' : `${entry.stagesCleared}/10 stages`}</small>
                <b>{entry.score.toLocaleString()}</b>
                <time>{formatTime(entry.durationMs)}</time>
              </li>
            ))}
          </ol>
          {isBackendConfigured() && !leaderboardError && leaderboardTotalPages > 1 && (
            <div class="leaderboard-pagination" aria-label="Leaderboard pages">
              <button class="secondary-button" type="button" disabled={leaderboardLoading || leaderboardPage <= 1} onClick={() => changeLeaderboardPage(leaderboardPage - 1)}>Previous</button>
              <span>Page {leaderboardPage} / {leaderboardTotalPages}</span>
              <button class="secondary-button" type="button" disabled={leaderboardLoading || leaderboardPage >= leaderboardTotalPages} onClick={() => changeLeaderboardPage(leaderboardPage + 1)}>Next</button>
            </div>
          )}
        </section>
      )}

      {settingsOpen && (
        <section class="modal-layer settings-layer screen-enter" role="dialog" aria-modal="true" aria-label="Settings">
          <div class="settings-copy"><p class="eyebrow">CONTROL ROOM</p><h2>Settings</h2><p>Saved locally. No committee meeting required.</p></div>
          <div class="settings-list">
            {([
              ['music', 'Music', 'Original civic-chaos loop'],
              ['sound', 'Sound effects', 'Scuttles, clangs, and dramatic bureaucracy'],
              ['reducedMotion', 'Reduced motion', 'Calmer UI transitions and poster movement'],
              ['leftHanded', 'Left-handed controls', 'Swap movement and action clusters'],
            ] as const).map(([key, label, description]) => (
              <label class="setting-row"><span><strong>{label}</strong><small>{description}</small></span><input type="checkbox" checked={settings[key]} onChange={(event) => updateSetting(key, event.currentTarget.checked)} /></label>
            ))}
            {([
              ['jumpButtonScale', 'Jump button size', 'Make the jump target easier to hit'],
              ['actionButtonScale', 'Action button size', 'Adjust ability and duck controls'],
            ] as const).map(([key, label, description]) => (
              <label class="setting-row setting-row--slider">
                <span><strong>{label}</strong><small>{description}</small></span>
                <span class="setting-slider">
                  <input
                    type="range"
                    min="75"
                    max="135"
                    step="5"
                    value={Math.round(clampButtonScale(settings[key]) * 100)}
                    onInput={(event) => updateSetting(key, Number(event.currentTarget.value) / 100)}
                    aria-label={label}
                  />
                  <b>{Math.round(clampButtonScale(settings[key]) * 100)}%</b>
                </span>
              </label>
            ))}
          </div>
          <button class="primary-button" type="button" onClick={() => setSettingsOpen(false)}>Done</button>
        </section>
      )}

      {disclaimerOpen && (
        <section class="modal-layer disclaimer-layer screen-enter" role="dialog" aria-modal="true" aria-label="Fictional satire disclaimer">
          <div><p class="eyebrow">BEFORE THE MIC CHECK</p><h2>Fictional satire.<br />Real-world chill.</h2></div>
          <p>Project Roach uses a recognisable illustrated Jantar Mantar backdrop, but every character, unit, slogan, institution, and incident shown is invented. It does not make claims about real people, parties, protests, elections, or authorities.</p>
          <button class="primary-button" type="button" onClick={acceptDisclaimer}>Understood — play on</button>
        </section>
      )}

      {!tabOwner && screen === 'playing' && (
        <section class="tab-warning" role="dialog"><strong>Another tab owns this run.</strong><span>Take over here to prevent conflicting saves.</span><button type="button" onClick={() => { tabCoordinator.current?.takeOwnership(); setTabOwner(true); }}>Take over</button></section>
      )}

      {toast && screen !== 'playing' && screen !== 'paused' && <div class="app-toast" role="status">{toast}</div>}
      <div class={`rotate-prompt${screen === 'playing' || screen === 'paused' ? ' rotate-prompt--active' : ''}`}>
        <RoachPoster compact />
        <strong>Turn the phone sideways.</strong>
        <span>The level needs landscape. Menus work either way.</span>
      </div>
    </main>
  );
}
